'use client';

/**
 * API namespace `coupons` (sub-namespace within parent).
 * Verbatim extraction from frontend/src/lib/api-client.ts (L8405-L8655).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-4, T-EXPORT, T-IMPORTS (see ledger).
 */

import { apiFetch } from '../core/fetch-client';

import type {
  AuthResponse, ApiError, RegisterRequest, LoginRequest, PublicUser,
  DashboardData, FeatureFlags, UpdateProfileInput, ChangePasswordInput,
  Setup2FAResult, Verify2FAResult, Login2FAResponse,
  ForgotPasswordInput, ResetPasswordInput, VerifyEmailInput,
  NetworkTree, WalletData, ConferencesData, CreateConferenceInput,
  CreateInterviewBody, InterviewCreatedResponse, InterviewListResponse,
  InviteBody, InviteResponse, JoinBody, JoinResponse,
  DownlineResult, QualificationResult, IDeposit, PayoutResult,
  ReconciliationResult,
  UserDepositStatus,
  CreditBalanceResponse, CreditHistoryResponse,
  CreditPurchaseRequest, CreditPurchaseResponse, CreditTransactionsResponse,
  SellerRegistrationRequest, SellerProfileResponse,
  FulfillmentConfigRequest, OnboardingStepRequest,
  SellerDashboardResponse, PublicStoreResponse,
  StoreBranding, StoreBrandingUpdate,
  SellerPayoutRequestBody, SellerPayoutBalanceResponse,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  OrderCreateRequest, OrderCreateRequestUnion,
  OrderResponse, OrderListResponse, ShipOrderRequest,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  NetworkStats, WalletTransaction, WalletStats,
  RoomParticipantStat, ConferenceStats, NetworkNode,
  AnnouncementDto, CreateAnnouncementBody, UpdateAnnouncementBody,
  KycApplicationDto, Paginated,
  DepositDto, DepositStatus,
  DepositNewPayload, PayoutDto, PayoutStatus, PayoutStatusChangePayload,
  AuditListQuery, PaginatedAuditEntries, AuditChainVerificationResult,
  KycApplication, KycReviewStatus, KycDocumentType,
  KycFatfInput, GeoLocationOption, ZodType, Landing,
  AccountingNetwork, AccountingNetworkBreakdown, AccountingSummary,
  PayoutRequestBody, PushSubscribeInput, PushUnsubscribeInput,
  AcademyTrack, AcademyCourseStatus, AcademyCourseDifficulty,
  AcademyCourse, AcademyCourseWithMeta, AcademyCatalogResponse,
  AcademyModuleListItem, AcademyModuleDetail, AcademyModuleResource,
  AcademyEnrollmentSummary, AcademyMyCourse, AcademyMyCertificate,
  AcademyVerifyResult, AcademyMarkProgressResponse, AcademyAdminCourse,
  IUserCertification, EntrepreneurProfile, EntrepreneurProfileInput,
  DiscoveryResponse,
  AccountingUserBalances, AccountingLedgerEntry,
  AccountBalanceResult, TrialBalanceRow, TrialBalanceResult,
  ReconciliationReport, ReconciliationStatusResponse,
  ReconciliationHistoryResponse, AccountingPostResult,
  UnsignedTransaction, SubmitTransactionResult,
  TransactionStatusResult, PrepareTransactionBody,
  SubmitTransactionBody, AwaitingSignaturePayout,
  WelcomeMessageResponse, WelcomeMessageInput,
  SavedAddressResponse, SavedAddressInput,
  ForbiddenWordResponse, ForbiddenWordInput,
  PublicRoomInfo, PublicRoomBySlugInfo,
} from '../core/shared-types';

import type {
  AdminUserDto, AdminUserDetailDto, AdminPurgeUserResultDto,
  FeatureFlagDto, AdminConferenceDto, LandingLimits, AdminMetricsDto,
  DepositChainInfo, UserDepositDto,
  CircuitState, CircuitBreakerMetricsDto,
  CircuitBreakerResetResultDto, CircuitBreakerStateChangePayload,
  TransactionLedgerEntryDto, TransactionDto,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  ReconciliationStatus,
} from '../core/types';

import type {
  ConferenceRoomResponse, InterviewRoomResponse,
  ReservationAvailabilitySlot, ReservationAvailability,
  ReservationSlot, BookingInput, ReservationCreated, ReservationListItem,
  DirectMessageDTO, SendDirectMessageInput, UploadedDirectFileDTO,
} from '../core/interfaces';

export const coupons = {
  /**
   * GET /api/market/coupons — paginated public coupon catalog.
   *
   * Returns `CouponListResponse` (`coupons: CouponResponse[]` + `total`
   * + `page` + `limit`). The backend clamps `page >= 1` and `limit` to
   * `[1, 100]` (defense in depth). The backend independently filters by
   * `status='approved'` + `active=true` + active seller + valid date
   * window (`startsAt <= now <= expiresAt` — defense-in-depth re-check
   * in the service for the case where the auto-expire cron hasn't run
   * yet).
   *
   * Authz: PUBLIC (no auth required). 403 `MARKET_PRODUCTS_DISABLED`
   * when the `market_products_enabled` feature flag is off (coupons
   * inherit the products gate — they're a market-publication feature
   * that depends on the products flag).
   *
   * Spec ref: SPEC-242 §3.5 (GET /market/coupons), §3.6.1 (catálogo
   *           público UI).
   */
  async list(
    filters: {
      page?: number;
      limit?: number;
      search?: string;
      category?: string;
      subcategory?: string;
      minPrice?: string;
      maxPrice?: string;
      sortBy?: 'newest' | 'price_low' | 'price_high';
    },
    signal?: AbortSignal,
  ): Promise<CouponListResponse> {
    const qs = new URLSearchParams();
    if (filters.page !== undefined) qs.set('page', String(filters.page));
    if (filters.limit !== undefined) qs.set('limit', String(filters.limit));
    if (filters.search && filters.search.trim().length > 0) {
      qs.set('search', filters.search.trim());
    }
    if (filters.category) qs.set('category', filters.category);
    if (filters.subcategory) qs.set('subcategory', filters.subcategory);
    if (filters.minPrice) qs.set('minPrice', filters.minPrice);
    if (filters.maxPrice) qs.set('maxPrice', filters.maxPrice);
    if (filters.sortBy) qs.set('sortBy', filters.sortBy);
    const query = qs.toString();
    return apiFetch<CouponListResponse>(
      query ? `/market/coupons?${query}` : '/market/coupons',
      { auth: false, signal },
    );
  },
  /**
   * GET /api/market/coupons/:couponId — public coupon detail.
   *
   * Returns `CouponResponse` — a privacy-filtered view of the coupon
   * (NO `sellerId` leak beyond the snapshot; the response includes
   * `sellerStoreName` + `sellerStoreSlug` so the detail page can render
   * the seller link without an extra `getPublicStore` roundtrip).
   *
   * Authz: PUBLIC. 404 if the coupon does not exist OR is not
   * `status='approved'` OR `active=false` OR the seller is inactive OR
   * the coupon is outside its `startsAt`/`expiresAt` window (the
   * backend hides pending/rejected/paused/expired coupons + coupons
   * from inactive sellers — INV-MKT-20 — defense in depth: don't leak
   * existence of pending/rejected coupons to anonymous visitors).
   * 403 `MARKET_PRODUCTS_DISABLED` when the flag is off.
   *
   * Spec ref: SPEC-242 §3.5 (GET /market/coupons/:couponId), §3.6.1
   *           (catálogo público UI — detalle del cupón).
   */
  async get(
    couponId: string,
    signal?: AbortSignal,
  ): Promise<CouponResponse> {
    return apiFetch<CouponResponse>(
      `/market/coupons/${encodeURIComponent(couponId)}`,
      { auth: false, signal },
    );
  },
  /**
   * POST /api/market/coupons/:couponId/purchase — buyer purchases a
   * coupon (CRITICAL atomic flow — debits buyer credits).
   *
   * Validates the body with `couponPurchaseSchema` (Track C #9 — Zod)
   * on the backend. The schema enforces the "both or neither" rule on
   * `recipientName` + `recipientEmail` (a partial gift makes no sense).
   * The URL path's `:couponId` is the canonical couponId — the backend
   * overrides the body's `couponId` (defense-in-depth vs a client
   * mismatch — the schema requires `couponId` in the body so we set
   * it here verbatim from the URL param).
   *
   * The service (`couponService.purchaseCoupon`) orchestrates the
   * atomic flow: validate coupon (approved + active + within window +
   * user hasn't exceeded `maxRedemptionsPerUser` + coupon hasn't
   * exceeded `maxRedemptions`) → debit buyer credits → create
   * `CouponPurchase` → increment `redemptionsCount` → Ledger
   * double-entry → AuditLog `COUPON_PURCHASED`. All inside
   * `withFinancialTransaction` (INV-SEC-04 atomicity).
   *
   * 2FA required (INV-SEC-06 §9.6 — financial-grade op: debits buyer
   * credits). The `requireRecent2FA` middleware runs BEFORE the
   * controller (wired in `routes/market.routes.ts`). On 403
   * `2FA_REQUIRED`, the frontend opens `TwoFactorFinancialDialog` +
   * retries the call after successful 2FA verification (same pattern
   * as `api.market.orders.create`).
   *
   * Idempotency (INV-SEC-03): the buyer SHOULD send an
   * `Idempotency-Key` HTTP header on the POST to make the call
   * idempotent (Redis SETNX, 24h TTL — same key returns the same
   * purchase without double-debiting). The frontend generates a fresh
   * UUID per purchase attempt via `safeUUID()`.
   *
   * Authz: `requireAuth` + `orderCreateLimiter` (10/h per user) +
   * `requireRecent2FA` (TOTP/wallet 2FA within last 5 min).
   *
   * Spec ref: SPEC-242 §3.4.1 (reglas de negocio — enforced in the
   *           service), §3.5 (POST /market/coupons/:couponId/purchase),
   *           §3.6.1 (gifting flow UI); SPEC-NEGOCIO §9.6 (INV-SEC-06
   *           — 2FA for sensitive ops) + §10.13 (Cuponera Externa).
   */
  async purchase(
    couponId: string,
    input: CouponPurchaseInput,
    options?: { idempotencyKey?: string; signal?: AbortSignal },
  ): Promise<CouponPurchaseResponse> {
    // The Zod schema requires `couponId` in the body — set it from the
    // URL param verbatim (the backend asserts the body's couponId
    // matches the URL's couponId — defense-in-depth vs a client
    // mismatch).
    const body = { ...input, couponId };
    return apiFetch<CouponPurchaseResponse>(
      `/market/coupons/${encodeURIComponent(couponId)}/purchase`,
      {
        method: 'POST',
        body: JSON.stringify(body),
        headers: options?.idempotencyKey
          ? { 'Idempotency-Key': options.idempotencyKey }
          : undefined,
        signal: options?.signal,
      },
    );
  },
  /**
   * GET /api/market/coupons/my-purchases — list the authenticated
   * buyer's coupon purchases (paginated, sorted by `purchasedAt` desc
   * — newest first).
   *
   * Returns `CouponPurchaseListResponse` (`purchases` + `total` +
   * `page` + `limit`). Each purchase includes the snapshot fields
   * (`couponTitle`, `couponBrandName`, `redemptionInstructions`,
   * `redemptionMethod`, etc.) so the buyer's "Mis Cupones" page can
   * render without an extra API roundtrip (the snapshot is
   * denormalized at purchase time + frozen — if the seller later
   * edits the coupon's instructions, the buyer still sees the
   * instructions that were in effect at purchase time).
   *
   * Authz: self only — backend verifies the purchase's `buyerUserId`
   * matches `req.user!.sub`.
   *
   * Spec ref: SPEC-242 §3.5 (GET /market/coupons/my-purchases), §3.6.4
   *           (detalle gift card UI — similar shape for coupons).
   */
  async myPurchases(
    filters?: { page?: number; limit?: number; status?: PurchaseStatus },
    signal?: AbortSignal,
  ): Promise<CouponPurchaseListResponse> {
    const qs = new URLSearchParams();
    if (filters?.page !== undefined) qs.set('page', String(filters.page));
    if (filters?.limit !== undefined) qs.set('limit', String(filters.limit));
    if (filters?.status) qs.set('status', filters.status);
    const query = qs.toString();
    return apiFetch<CouponPurchaseListResponse>(
      query ? `/market/coupons/my-purchases?${query}` : '/market/coupons/my-purchases',
      { signal },
    );
  },
};
